import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { Switch } from '@sohanscript/ui/switch';
import { createFileRoute } from '@tanstack/react-router';
import { useRef } from 'react';
import { z } from 'zod';
import { Button } from '#/components/ui/button.tsx';
import { getAppEnv } from '#/lib/utils/app-env.ts';
import { Resume } from './-components/resume';

export const Route = createFileRoute('/_main/resume/')({
	component: RouteComponent,
	validateSearch: z.object({
		as: z.enum(['fe', 'be', 'fs', 'se', 'sd', 'wd']).default('se'),
		useColor: z.boolean().default(true),
		size: z.enum(['lg', 'sm']).default('sm'),
		edit: z.boolean().optional(),
	}),
});

function printResume(el: HTMLElement, useColor = false) {
	const bgColor = useColor ? getComputedStyle(el).backgroundColor : 'white';

	const clone = el.cloneNode(true) as HTMLElement;
	clone.style.margin = '0';
	clone.style.width = '100%';
	clone.style.maxWidth = '100%';
	clone.style.minHeight = '';
	clone.style.boxShadow = 'none';

	const win = window.open('', '_blank');
	if (!win) return;

	document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
		win.document.head.appendChild(node.cloneNode(true));
	});

	const pageStyle = win.document.createElement('style');
	pageStyle.textContent = `
		@page { size: A4 portrait; margin: 10mm 0 0 0; background: ${bgColor}; }
		@page :first { margin: 0; }
		body { margin: 0; padding: 0; background: ${bgColor}; } }
		* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		section { break-inside: avoid; }
	`;
	win.document.head.appendChild(pageStyle);
	win.document.body.appendChild(clone);
	if (!useColor) win.document.body.classList.add('resume-white-theme');

	setTimeout(() => {
		win.focus();
		win.print();
		win.close();
	}, 250);
}

function RouteComponent() {
	const resumeRef = useRef<HTMLDivElement>(null);
	const navigate = Route.useNavigate();
	const search = Route.useSearch();

	async function handleDownload() {
		if (resumeRef.current) printResume(resumeRef.current, search.useColor);
	}
	return (
		<>
			{getAppEnv('preview') && (
				<div
					style={{ maxWidth: '794px' }}
					className="absolute inset-x-0 mx-auto flex justify-end"
				>
					<Button
						className="translate-y-px rounded-t-full border-b-0 hover:translate-y-px"
						tooltip={m.resume_edit()}
						size="icon"
						variant="opacity"
						onClick={() =>
							navigate({
								search: { ...search, edit: !search.edit },
								resetScroll: false,
							})
						}
					>
						<Iconify
							icon={search.edit ? 'lucide:save' : 'lucide:pen'}
							className="size-4.5"
						/>
					</Button>
				</div>
			)}
			<Resume
				className={cn(search.edit && 'outline-none ring-3 ring-destructive')}
				contentEditable={search.edit}
				ref={resumeRef}
			/>
			<div className="mt-10 flex justify-center">
				<div className="flex items-center gap-3 border border-border bg-card px-4 py-3 shadow-sm">
					<label
						htmlFor="color-toggle"
						className="flex cursor-pointer select-none items-center gap-2 font-medium text-muted-foreground text-sm hover:text-secondary"
					>
						<Switch
							id="color-toggle"
							checked={search.useColor}
							onCheckedChange={(v) => {
								navigate({ search: { useColor: v }, resetScroll: false });
							}}
						/>
						{m.resume_with_color()}
					</label>

					<div className="h-5 w-px bg-border" />

					<Button onClick={() => handleDownload()} icon="lucide:download">
						{m.resume_download_pdf()}
					</Button>
				</div>
			</div>
		</>
	);
}
